import type { HelpTopic } from '../../modules/core/index.js'

/** The AI briefing's help topics (docs/plans/in-app-help-plan.md): the panel for staff, the usage page for the admin. */
export const BRIEFINGS_HELP_TOPICS: readonly HelpTopic[] = [
  {
    id: 'ai_briefings.panel',
    title: 'Your AI briefing',
    seats: ['operator', 'facility_administrator'],
    summary: 'What changed since you last signed in, or the Insights reports for a span, written by AI from the records the platform keeps, with a few questions you can ask it.',
    sections: [
      {
        heading: 'Choose first',
        points: [
          'The dialog writes nothing until you choose: this facility or all your facilities since you signed in from a Dashboard, or this month, this quarter or this year of the Insights reports from the Insights page.',
          'A choice already written opens at once, with when it was written on its card.',
        ],
      },
      {
        heading: 'What it reads',
        points: [
          'What needs you (the Today tab), what happened (the Activity tab), where the month stands (the Month tab), what settings moved (names and fields only, never the values or who), the Clinical Bridge callouts and the roster totals.',
          'Every sentence rests on a numbered fact from those records. A sentence that cites no fact is dropped before you see it, and every link is a fact\'s own, so the briefing cannot send you somewhere the records did not.',
          'A number can still be copied wrongly. The footer says so: check before acting.',
        ],
      },
      {
        heading: 'Why it is read, not refreshed',
        points: [
          'Your sign-in is the window: one briefing per sign-in, answered again on every press without a new call.',
          'Refresh writes a new one only where the feed or the decisions hold something newer, and not within the cooldown of the last. The reason and the time are shown on the button.',
          'There are so many briefings, questions and emails a day for you and for your organization; the limits are your organization\'s settings, not the app\'s.',
        ],
      },
      {
        heading: 'Questions',
        points: [
          'A question is answered from this briefing\'s facts and nothing else: no other facility, no history, no action taken.',
          'One the facts do not cover is answered "That isn\'t in this briefing" with where to look.',
          'The count left is shown by the box; one question is answered at a time.',
        ],
      },
      {
        heading: 'Email me this',
        points: [
          'The email goes to your own login address and nowhere else, once per briefing, with the links and the questions answered so far.',
          '"Always email my briefing" in your notification settings sends each one as soon as it is ready.',
        ],
      },
      {
        heading: 'Across your facilities',
        seats: ['operator'],
        points: ['The sparkle on the facilities list writes one briefing across every facility: the facilities needing you first and in full, the rest a line each.'],
      },
      {
        heading: 'The Insights summary',
        points: [
          'AI summary on the Insights page reads every report over the span you choose: the portfolio, reliability, tiers, attendance, coverage, census and workforce, the last ninety days for a quarter or year, and the settings changes counted.',
          'It is written from the month rollups Insights keeps, so a month is in it once it has been added up; Refresh writes again once a month is counted anew.',
        ],
      },
      {
        heading: 'What leaves the platform',
        points: ['Worker names as the feed shows them, facility and unit names, counts, dates, the engine\'s reasons and your questions as typed. Never a credential, an email, a phone, a resident or a settings value. Anthropic does not train on what is sent.'],
      },
    ],
    related: ['schedule.today', 'roster.dashboard'],
  },
  {
    id: 'ai_briefings.usage',
    title: 'AI usage',
    seats: ['admin'],
    summary: 'What the AI briefings cost the deployment, by day and by client: counts, tokens and the estimate at the configured prices.',
    sections: [
      {
        heading: 'What is counted',
        points: [
          'Briefings written, questions answered, emails sent, empty windows (no call made), failures and refusals by the guard, with the tokens each call used.',
          'The cost is an estimate from the deployment\'s price parameters; without them, tokens only. The deployment\'s daily cap and the model are shown with it.',
          'A client\'s own limits are the settings block on its license, each only ever tightening the deployment\'s default; set them on the client\'s Add-ons section.',
          'A briefing\'s words and questions are the reader\'s own and are never shown here.',
        ],
      },
    ],
  },
]
